import React, { useState } from 'react'
import Table from '../../../components/common/Table/Table'
import FilterComponent, {
  FilterData,
} from '../../../components/common/FilterComponent/FilterComponent'
import CalendarWeekView, {
  EmployeeTimeOffRequest,
} from '../../../components/common/CalendarWeekView/CalendarWeekView'
import { useNavigate, Link } from 'react-router-dom'
import CalendarStatusInfo from '../../../components/common/CalendarStatusInfo'
import StatusIconWithTooltip from '../../../components/common/StatusIconWithTooltip'
import LabeledValue from '../../../components/common/LabeledValue'
import { usePrint } from '../../../hooks/usePrint'
import { useProfileDisplay } from '../../../hooks/useProfileDisplay'
import ValidationBanner from '../../../components/common/ValidationBanner'
import Loader from '../../../components/common/Loader'
import { useAppDispatch, useAppSelector } from '../../../store/hooks'
import { RootState } from '../../../store/store'
import {
  getApproverFilterOptions,
  mapApproverFilterOptionsResponse,
} from '../../../store/slices/Approver/approverFilterOptionsSlice'
import {
  selectAppliedFilter,
  selectDateFilterMode,
  setAppliedFilter,
  setDateFilterMode,
} from '../../../store/slices/Approver/approverAppliedFiltersSlice'
import {
  getApproverRequests,
  getApproverPrintRequests,
  type ApproverRequestsPayload,
  type ApproverRequestItem,
  type ApproverSortBy,
  type ApproverSortOrder,
  DEFAULT_APPROVER_STATUS_CODES,
  DEFAULT_APPROVER_REQUEST_TYPE_CODES,
} from '../../../store/slices/Approver/approverRequestsSlice'
import { exportApproverRequests } from '../../../store/slices/Approver/approverExportSlice'
import { getStatusCode } from '../../../utils/requestStatus'
import { getTimeOffTypeCode } from '../../../utils/timeOffTypes'
import {
  getApproverCalendarView,
  mapApproverCalendarViewResponse,
  DEFAULT_APPROVER_STATUS_CODES as DEFAULT_APPROVER_CALENDAR_STATUS_CODES,
  DEFAULT_APPROVER_REQUEST_TYPES as DEFAULT_APPROVER_CALENDAR_TYPES,
  type ApproverCalendarPayload,
} from '../../../store/slices/Approver/approverCalendarViewSlice'
import { formatLegacyDateTime, formatSeniorityDate } from '../../../utils/date'

// Asset paths (Vite public assets)
const Print = '/assets/images/printer-outline.svg'
const exportIcon = '/assets/images/download-outline.svg'
const eyeIcon = '/assets/images/eye-frame-outline.svg'
const eyeOffIcon = '/assets/images/eye-off-outline.svg'
const downIcon = '/assets/images/chevrondown.svg'
const listIcon = '/assets/images/list-outline.svg'
const calendarIcon = '/assets/images/calendar-dates-outline.svg'
const plusIcon = '/assets/images/plus-circle-outline.svg'
const minusIcon = '/assets/images/minus-circle-outline.svg'

const nextWeek = new Date()
nextWeek.setDate(nextWeek.getDate() + 7)
// ---------------------------------------------------------------------------
// Payload builder helpers (module-level so they are tree-shakeable and testable)
// ---------------------------------------------------------------------------

/**
 * Extract the code portion from a "code - description" formatted label.
 * E.g., "0110 - Med/Surg Float" → "0110"
 */
const extractCodeFromLabel = (label: string): string => {
  const idx = label.indexOf(' - ')
  return idx === -1 ? label.trim() : label.slice(0, idx).trim()
}

/**
 * Extract the trailing ID from a "Name - ID" formatted label.
 * E.g., "Bauer, Camille - 00053997" → "00053997"
 */
const extractIdFromEndOfLabel = (label: string): string => {
  const parts = label.split(' - ')
  return parts.length > 1 ? (parts.at(-1)?.trim() ?? label) : label
}

/**
 * Convert a MM/DD/YYYY date string to YYYY-MM-DD for the API payload.
 * Returns the original string unchanged when the format does not match.
 */
const toApiDate = (date: string): string => {
  const [mm, dd, yyyy] = date.split('/')
  if (!mm || !dd || !yyyy) return date
  return `${yyyy}-${mm.padStart(2, '0')}-${dd.padStart(2, '0')}`
}

/**
 * Maps the column key used in the Table component to the sort field name
 * accepted by POST /tort/approver/time-off-requests.
 * Returns undefined for columns that are not sortable via the API.
 */
const COLUMN_KEY_TO_SORT_BY: Record<string, ApproverSortBy> = {
  employeeNuid: 'empNUID',
  dateRequested: 'dateRequested',
  hoursRequested: 'hoursRequested',
  dateSubmitted: 'dateSubmitted',
  shift: 'shift',
  unionSeniority: 'unionSeniority',
  alternateSeniority: 'altSeniority',
  rank: 'rank',
  requestType: 'requestType',
  status: 'status',
}

/**
 * Builds the full POST body for POST /tort/approver/time-off-requests from
 * the UI filter state and pagination parameters.
 *
 * - Status and request-type labels from FilterComponent are mapped to their
 *   corresponding API codes using the shared utility functions.
 * - When no specific values are selected, all defaults are sent so the API
 *   returns the full unfiltered dataset.
 * - Optional filters (department, union, jobCode, approvalGroup) are only
 *   included when a specific (non-"All") value is selected.
 *
 * @param page        - 1-based page number.
 * @param perPage     - Page size (default 20).
 * @param filterData  - Applied filter from FilterComponent, or null for no filter.
 * @param dateMode    - "all" (dateOption="A") or "range" (dateOption="R").
 * @param sortBy      - Optional column sort field.
 * @param sortOrder   - "ASC" or "DESC".
 */
/**
 * Apply optional single-value filters (department, union, jobCode, approvalGroup)
 * to the filters object — only when a specific (non-"All") value is selected.
 * Mutates `filters` in-place for clarity.
 */
const applyOptionalFilters = (
  filters: ApproverRequestsPayload['filters'],
  checkboxFilters: Record<string, string[]>
): void => {
  if (!filters) return

  const pickValues = (arr?: string[]): string[] | undefined => {
    const selected = (arr ?? []).filter((v) => !/^all/i.test(v))
    return selected.length ? selected.map(extractCodeFromLabel) : undefined
  }

  const department = pickValues(checkboxFilters.departmentFilter)
  if (department) {
    filters.department = department
  }
  const union = pickValues(checkboxFilters.unionAffiliationFilter)
  if (union) {
    filters.union = union
  }
  const jobCode = pickValues(checkboxFilters.jobCodeFilter)
  if (jobCode) {
    filters.jobCode = jobCode
  }
  const approvalGroup = (checkboxFilters.approvalGroupFilter ?? [])
    .filter((v) => !/^all/i.test(v))
    .map(extractIdFromEndOfLabel)

  if (approvalGroup.length) {
    filters.approvalGroup = approvalGroup
  }
}

const buildApproverPayload = ({
  page = 1,
  perPage = 20,
  filterData,
  dateMode = 'all',
  sortBy,
  sortOrder = 'ASC',
}: {
  page?: number
  perPage?: number
  filterData?: FilterData | null
  dateMode?: 'all' | 'range'
  sortBy?: ApproverSortBy
  sortOrder?: ApproverSortOrder
}): ApproverRequestsPayload => {
  const now = new Date()
  const date = `${now.getFullYear()}-${String(now.getMonth() + 1).padStart(2, '0')}-${String(now.getDate()).padStart(2, '0')}`

  const checkboxFilters = filterData?.checkboxFilters ?? {}

  // Map status UI labels → API codes; fall back to all codes when none selected
  const selectedStatuses = (checkboxFilters.statusFilter ?? [])
    .filter((label) => !/^all/i.test(label))
    .map((label) => getStatusCode(label))
    .filter(Boolean) as typeof DEFAULT_APPROVER_STATUS_CODES

  // Map request-type UI labels → API codes; fall back to all codes when none selected
  const selectedTypes = (checkboxFilters.requestTypeFilter ?? [])
    .filter((label) => !/^all/i.test(label))
    .map((label) => getTimeOffTypeCode(label))
    .filter(Boolean) as typeof DEFAULT_APPROVER_REQUEST_TYPE_CODES

  const statusArray =
    selectedStatuses.length > 0
      ? selectedStatuses
      : DEFAULT_APPROVER_STATUS_CODES
  const requestTypeArray =
    selectedTypes.length > 0
      ? selectedTypes
      : DEFAULT_APPROVER_REQUEST_TYPE_CODES

  const payload: ApproverRequestsPayload = {
    date,
    filters: {
      dateOption: dateMode === 'range' ? 'R' : 'A',
      status: statusArray,
      requestType: requestTypeArray,
    },
    page: String(page),
    perPage: String(perPage),
  }

  // Conditional date range fields — only when dateOption is "R"
  if (
    dateMode === 'range' &&
    filterData?.startDate &&
    filterData.endDate &&
    payload.filters
  ) {
    payload.filters.startDate = toApiDate(filterData.startDate)
    payload.filters.endDate = toApiDate(filterData.endDate)
  }

  // Apply optional single-value filters via extracted helper
  applyOptionalFilters(payload.filters, checkboxFilters)

  if (sortBy) payload.sortBy = sortBy
  if (sortOrder) payload.sortOrder = sortOrder

  return payload
}
const buildShowAllApproverPayload = ({
  page = 1,
  perPage = 20,
  sortOrder = 'ASC',
}: {
  page?: number
  perPage?: number
  sortOrder?: ApproverSortOrder
}): ApproverRequestsPayload => {
  const now = new Date()

  const date = `${now.getFullYear()}-${String(now.getMonth() + 1).padStart(2, '0')}-${String(now.getDate()).padStart(2, '0')}`

  return {
    date,
    page: String(page),
    perPage: String(perPage),
    sortOrder,
  }
}
const buildApproverCalendarPayload = ({
  filterData,
  dateMode = 'all',
  selectedWeekDate,
}: {
  filterData?: FilterData | null
  dateMode?: 'all' | 'range'
  selectedWeekDate?: Date
}) => {
  const today = new Date()

  const date = `${today.getFullYear()}-${String(
    today.getMonth() + 1
  ).padStart(2, '0')}-${String(today.getDate()).padStart(2, '0')}`


  let selected = selectedWeekDate

  // Date range selected -> selectedDate must be startDate

// Only use range startDate when no calendar week was explicitly selected
if (
  dateMode === 'range' &&
  filterData?.startDate
) {
  selected = new Date(toApiDate(filterData.startDate))
}


  // First load -> today + 7 days
  if (!selected) {
    selected = new Date()
    selected.setDate(selected.getDate() + 7)
  }


  const selectedDate = `${selected.getFullYear()}-${String(
    selected.getMonth() + 1
  ).padStart(2, '0')}-${String(selected.getDate()).padStart(2, '0')}`


  const checkboxFilters = filterData?.checkboxFilters ?? {}


  const selectedStatuses = (checkboxFilters.statusFilter ?? [])
    .filter((label) => !/^all/i.test(label))
    .map((label) => getStatusCode(label))
    .filter(
      (code): code is (typeof DEFAULT_APPROVER_CALENDAR_STATUS_CODES)[number] =>
        Boolean(code)
    )


  const selectedTypes = (checkboxFilters.requestTypeFilter ?? [])
    .filter((label) => !/^all/i.test(label))
    .map((label) => getTimeOffTypeCode(label))
    .filter(
      (code): code is (typeof DEFAULT_APPROVER_CALENDAR_TYPES)[number] =>
        Boolean(code)
    )


  const payload: ApproverCalendarPayload = {
    date,
    selectedDate,

    filters: {
      dateOption: dateMode === 'range' ? 'R' : 'A',

      status: selectedStatuses.length
        ? selectedStatuses
        : DEFAULT_APPROVER_CALENDAR_STATUS_CODES,

      requestType: selectedTypes.length
        ? selectedTypes
        : DEFAULT_APPROVER_CALENDAR_TYPES,
    },
  }


  if (dateMode === 'range') {
    if (filterData?.startDate && filterData.endDate) {
      payload.filters.startDate = toApiDate(filterData.startDate)
      payload.filters.endDate = toApiDate(filterData.endDate)
    } else {
      // No explicit filter range present — use the computed selectedDate
      // so the API receives valid start/end values and avoids validation
      // errors on first load.
      payload.filters.startDate = selectedDate
      payload.filters.endDate = selectedDate
    }
  }


  applyOptionalFilters(payload.filters, checkboxFilters)


  return payload
}
// ---------------------------------------------------------------------------
// Component
// ---------------------------------------------------------------------------

/** Props injected by ApproverTabs via the Tabs component's extraProps. */
interface ApproverRequestsProps {
  /**
   * Callback to update the tab badge count in the parent ApproverTabs component.
   * Called whenever the API returns a new total record count.
   */
  updateDataCount?: (tabId: number, count: number) => void
}

/**
 * Incoming Time Off Requests tab for approvers.
 *
 * On mount, calls POST /tort/approver/time-off-requests with default filters
 * (all statuses, all request types, dateOption="A") to load the initial list.
 * When the user applies a filter via FilterComponent, re-fetches from page 1
 * with the mapped filter payload. Pagination and column sorting also trigger
 * new API calls.
 *
 * Filter dropdown options are fetched separately from
 * GET /tort/approver/time-off-requests/filter-options and passed to
 * FilterComponent via the filtersConfig prop.
 */
const ApproverRequests: React.FC<ApproverRequestsProps> = ({
  updateDataCount,
}) => {
  const dispatch = useAppDispatch()

  // --- Filter dropdown options ---
  const filterOptionsData = useAppSelector(
    (state: RootState) => state.approverFilterOptions.data
  )
  const filterOptionsError = useAppSelector(
    (state: RootState) => state.approverFilterOptions.error
  )

  // calendar view states //
  const calendarData = useAppSelector(
    (state: RootState) => state.approverCalendarView.data
  )

  const calendarLoading = useAppSelector(
    (state: RootState) => state.approverCalendarView.loading
  )

  const exportLoading = useAppSelector(
    (state: RootState) => state.approverExport.loading
  )
  // --- Incoming requests state ---
  const items =
    useAppSelector((state: RootState) => state.approverRequests.items) ?? []
  const requestsLoading =
    useAppSelector((state: RootState) => state.approverRequests.loading) ??
    false
  const requestsError = useAppSelector(
    (state: RootState) => state.approverRequests.error
  )
  const currentPage =
    useAppSelector((state: RootState) => state.approverRequests.currentPage) ??
    1
  const lastPayload = useAppSelector(
    (state: RootState) => state.approverRequests.lastPayload
  )
  const pageSize =
    useAppSelector((state: RootState) => state.approverRequests.pageSize) ?? 20
  const totalRecords =
    useAppSelector((state: RootState) => state.approverRequests.totalRecords) ??
    0
    const calendarTotalRecords = useAppSelector(
  (state: RootState) =>
    state.approverCalendarView.totalRecords
)
  const sortOrder =
    useAppSelector((state: RootState) => state.approverRequests.sortOrder) ??
    'ASC'
  const sortBy = useAppSelector(
    (state: RootState) => state.approverRequests.sortBy
  )
  const navigate = useNavigate()
  // --- Redux state for filter persistence ---
  const reduxAppliedFilter = useAppSelector(selectAppliedFilter)
  const reduxDateFilterMode = useAppSelector(selectDateFilterMode)

  const handleSetAppliedFilter = React.useCallback(
    (filter: FilterData | null) => {
      dispatch(setAppliedFilter(filter))
    },
    [dispatch]
  )

  const handleSetDateFilterMode = React.useCallback(
    (mode: 'all' | 'range') => {
      dispatch(setDateFilterMode(mode))
    },
    [dispatch]
  )

  // --- Local UI state ---
const [view, setView] = useState<'list' | 'calendar'>(
  () => (sessionStorage.getItem('incomingView') as 'list' | 'calendar') || 'list'
);
  const isCalendarView = view === 'calendar'
  const [showSeniority, setShowSeniority] = React.useState(false)
  const [isFilterOpen, setIsFilterOpen] = React.useState(false)
  const [activeSortColumn, setActiveSortColumn] = React.useState<
    string | undefined
  >(undefined)
  const [activeSortDirection, setActiveSortDirection] =
    React.useState<ApproverSortOrder>('ASC')
  const [isShowingAllRequests, setIsShowingAllRequests] = React.useState(false)
  const [isPrinting, setIsPrinting] = React.useState(false)
  const [printSortBy, setPrintSortBy] = React.useState<
    ApproverSortBy | undefined
  >(undefined)
  const [printSortOrder, setPrintSortOrder] =
    React.useState<ApproverSortOrder>('ASC')
  const [filterError, setFilterError] = React.useState<string | null>(null)
  const filterErrorRef = React.useRef<HTMLDivElement>(null)
  const [calendarWeekStart, setCalendarWeekStart] = React.useState<Date>(() => {
    const nextWeek = new Date()
    nextWeek.setDate(nextWeek.getDate() + 7)
    return nextWeek
  })
  const [printRows, setPrintRows] = React.useState<ApproverRequestItem[]>([])
  const [calendarStartDate, setCalendarStartDate] = React.useState('')
  /**
   * Dispatch a new fetch for the given page, using the provided filter/sort
   * overrides (or falling back to current state). Explicit overrides are
   * necessary to avoid stale-closure bugs when called immediately after
   * updating React state (e.g., inside handleApplyFilter).
   */
  const fetchRequests = React.useCallback(
    (
      page: number,
      filterOverride?: FilterData | null,
      dateModeOverride?: 'all' | 'range',
      sortByOverride?: ApproverSortBy,
      sortOrderOverride?: ApproverSortOrder,
      perpageOverride?: number
    ) => {
      const payload = buildApproverPayload({
        page,
        filterData:
          filterOverride === undefined ? reduxAppliedFilter : filterOverride,
        dateMode: dateModeOverride ?? reduxDateFilterMode,
        sortBy: sortByOverride ?? sortBy,
        sortOrder: sortOrderOverride ?? sortOrder,
        perPage: perpageOverride ?? pageSize,
      })

      return dispatch(getApproverRequests(payload))
    },
    [dispatch, reduxAppliedFilter, reduxDateFilterMode, sortBy, sortOrder, pageSize]
  )

  React.useEffect(() => {
  sessionStorage.setItem('incomingView', view);
}, [view]);
  const fetchCalendar = React.useCallback(
    (
      filterOverride?: FilterData | null,
      dateModeOverride?: 'all' | 'range',
      weekDate?: Date
    ) => {
      const payload = buildApproverCalendarPayload({
        filterData:
          filterOverride === undefined ? reduxAppliedFilter : filterOverride,
      dateMode: dateModeOverride ?? reduxDateFilterMode,
       selectedWeekDate:
            dateModeOverride === 'range' && filterOverride?.startDate
              ? undefined
              : weekDate ?? calendarWeekStart,
      })

      dispatch(getApproverCalendarView(payload))
    },
    [dispatch, reduxAppliedFilter, reduxDateFilterMode, calendarWeekStart]
  )

  /**
   * Fetch filter dropdown options on mount.
   * Aborts the in-flight request on unmount to avoid state-after-unmount errors.
   */
  React.useEffect(() => {
    const promise = dispatch(getApproverFilterOptions())
    return () => {
      promise.abort()
    }
  }, [dispatch])

  /**
   * On mount: restore persisted filters from sessionStorage into Redux, then
   * fire the initial request using those filters.
   */
  React.useEffect(() => {
    const persistedFilter = (() => {
      try {
        const stored = sessionStorage.getItem('approver_applied_filters')
        if (!stored) return null
        const parsed = JSON.parse(stored)
        if (
          typeof parsed === 'object' &&
          parsed !== null &&
          'checkboxFilters' in parsed
        ) {
          return parsed as FilterData
        }
      } catch {
        // Ignore parse errors
      }
      return null
    })()

    const persistedMode = (() => {
      try {
        const stored = sessionStorage.getItem('approver_date_filter_mode')
        if (stored === 'range' || stored === 'all') {
          return stored
        }
      } catch {
        // Ignore errors
      }
      return 'all'
    })() as 'all' | 'range'

    if (persistedFilter) {
      handleSetAppliedFilter(persistedFilter)
      handleSetDateFilterMode(persistedMode)
    }

    // Use the locally-read sessionStorage value to build the payload so that
    // persisted filters are applied on the very first request after a reload.
    if (lastPayload) {
      dispatch(getApproverRequests(lastPayload))
    } else if (persistedFilter) {
      dispatch(
        getApproverRequests(
          buildApproverPayload({ page: 1, filterData: persistedFilter, dateMode: persistedMode })
        )
      )
    } else {
      dispatch(getApproverRequests(buildApproverPayload({ page: 1 })))
    }
  }, [dispatch]) // eslint-disable-line react-hooks/exhaustive-deps
  React.useEffect(() => {
    if (view === 'calendar') {
      fetchCalendar(undefined, undefined, calendarWeekStart)
    }
  }, [view, calendarWeekStart, fetchCalendar])

  /**
   * Keep the ApproverTabs badge count in sync with the total from the API
   * (tab ID 1 = Incoming Time Off Requests).
   */
React.useEffect(() => {
  const count = isCalendarView
    ? calendarTotalRecords
    : totalRecords

  updateDataCount?.(1, count)
}, [
  isCalendarView,
  calendarTotalRecords,
  totalRecords,
  updateDataCount,
])

  /**
   * Map the raw filter-options API response to the shape FilterComponent
   * expects. Returns undefined while loading so FilterComponent falls back
   * to the bundled checkboxFilters.json defaults.
   */
  const filtersConfig = React.useMemo(
    () =>
      filterOptionsData
        ? mapApproverFilterOptionsResponse(filterOptionsData)
        : undefined,
    [filterOptionsData]
  )
  const defaultInitialFilter: FilterData | undefined = React.useMemo(() => {
    if (!filtersConfig) return undefined

    const checkboxFilters: Record<string, string[]> = {}

    Object.keys(filtersConfig).forEach((filterKey) => {
      const group = filtersConfig[filterKey]

      if (group?.labels?.length) {
        // Default: all checkbox values selected
        checkboxFilters[filterKey] = [...group.labels]
      }
    })

    // Override only Status with default approver statuses
    const statusLabels =
      filtersConfig.statusFilter?.labels?.filter((label) => {
        const code = getStatusCode(label)
        return DEFAULT_APPROVER_STATUS_CODES.includes(
          code as (typeof DEFAULT_APPROVER_STATUS_CODES)[number]
        )
      }) ?? []

    checkboxFilters.statusFilter = statusLabels

    return {
      startDate: '',
      endDate: '',
      checkboxFilters,
    }
  }, [filtersConfig])

  React.useEffect(() => {
    if (!reduxAppliedFilter && defaultInitialFilter) {
      handleSetAppliedFilter(defaultInitialFilter)
    }
  }, [defaultInitialFilter, reduxAppliedFilter, handleSetAppliedFilter])

  // Get columns based on seniority visibility
  const getColumns = () => {
    const baseColumns = [
      {
        key: 'employeeNuid',
        label: 'Employee (NUID)',
        isSortable: true,
        className: '!whitespace-normal !w-auto !min-w-0 !max-w-none',
      },
      {
        key: 'dateRequested',
        label: 'Dates\nRequested',
        className: '!whitespace-pre-wrap !w-auto !min-w-0 !max-w-none',
        icon: downIcon,
        isSortable: true,
      },
      {
        key: 'hoursRequested',
        label: 'Hours\nRequested',
        className: '!whitespace-pre-wrap !w-auto !min-w-0 !max-w-none',
        icon: downIcon,
        isSortable: true,
      },
      {
        key: 'requestType',
        label: 'Request Type',
        className: '!whitespace-pre-wrap !w-auto !min-w-0 !max-w-none',
        icon: downIcon,
        isSortable: true,
      },
      { key: 'status', label: 'Request Status',className: '!whitespace-pre-wrap !w-auto !min-w-0 !max-w-none', icon: downIcon, isSortable: true, },
      {
        key: 'dateSubmitted',
        label: 'Date\nSubmitted',
          className:
    '!whitespace-pre-wrap !w-auto !min-w-0 !max-w-none md:!w-[110px] md:!min-w-[110px]',
        icon: downIcon,
        isSortable: true,
      },
      { key: 'detail', label: 'Detail', className: '!whitespace-normal !w-auto !min-w-0 !max-w-none break-words', icon: downIcon },
      {
        key: 'shift',
        label: 'Shift',
        className: '!whitespace-normal !w-auto !min-w-0 !max-w-none',
        icon: downIcon,
        isSortable: true,
      },
      {
        key: 'unionSeniority',
        label: 'Union\nSeniority',
        className: '!whitespace-pre-wrap !w-auto !min-w-0 !max-w-none',
        icon: downIcon,
        isSortable: true,
      },
      {
        key: 'alternateSeniority',
        label: 'Alternate\nSeniority',
        className: '!whitespace-pre-wrap !w-auto !min-w-0 !max-w-none',
        icon: downIcon,
        isSortable: true,
      },
      {
        key: 'rank',
        label: 'Vac Bid Rank',
        className: '!whitespace-pre-wrap !w-auto !min-w-0 !max-w-none',
        icon: downIcon,
        isSortable: true,
      },
    ]
    return showSeniority
      ? baseColumns
      : baseColumns.filter(
          (col) =>
            !['unionSeniority', 'alternateSeniority', 'rank'].includes(col.key)
        )
  }

  const getColumnsForPrint = () => {
    const baseColumns = [
      { key: 'employeeNuid', label: 'Employee (NUID)' },
      {
        key: 'dateRequested',
        label: 'Dates\nRequested',
        className: '!whitespace-pre-wrap md:!min-w-[110px] md:!max-w-[130px]',
        icon: downIcon,
      },
      {
        key: 'hoursRequested',
        label: 'Hours\nRequested',
        className: '!whitespace-pre-wrap md:!min-w-[100px]',
        icon: downIcon,
      },
      {
        key: 'requestType',
        label: 'Request Type',
        className: 'md:!min-w-[140px]',
        icon: downIcon,
      },
      { key: 'status', label: 'Request Status', className: 'md:!min-w-[140px]', icon: downIcon },
      {
        key: 'dateSubmitted',
        label: 'Date\nSubmitted',
        className: '!whitespace-pre-wrap',
        icon: downIcon,
      },
      { key: 'detail', label: 'Detail', className: '', icon: downIcon },
      {
        key: 'shift',
        label: 'Shift',
        className: '!whitespace-nowrap md:!min-w-[90px]',
        icon: downIcon,
      },
      {
        key: 'unionSeniority',
        label: 'Union\nSeniority',
        className: '!whitespace-pre-wrap md:!min-w-[100px]',
        icon: downIcon,
      },
      {
        key: 'alternateSeniority',
        label: 'Alternate\nSeniority',
        className: '!whitespace-pre-wrap md:!min-w-[110px]',
        icon: downIcon,
      },
      {
        key: 'rank',
        label: 'Vac Bid Rank',
        className: '!whitespace-pre-wrap md:!min-w-[120px]',
        icon: downIcon,
      },
    ]
    return showSeniority
      ? baseColumns
      : baseColumns.filter(
          (col) =>
            !['unionSeniority', 'alternateSeniority', 'rank'].includes(col.key)
        )
  }

  /**
   * Maps API response items to the row shape expected by the Table component.
   * Injects the navigation link into the date-requested cell and the status
   * icon beside the employee NUID.
   */
  const mapItemsToRows = (apiItems: ApproverRequestItem[]) =>
    apiItems.map((item) => ({
      id: item.requestId,
      employeeNuid: (
        <div className="flex items-center gap-2 w-full min-w-0">
          <span>{item.employeeName}</span>
          <div className="!w-[50px] !min-w-[50px] !max-w-[50px]">
            <StatusIconWithTooltip
              statusCode={item.statusCode}
              alt={item.status}
            />
          </div>
        </div>
      ),
      dateRequested: (
        <Link
          to={`/approver/process/${item.requestId}`}
          className="break-normal"
          style={{ color: '#0074AD' }}
        >
          {item.toDate}
        </Link>
      ),
      hoursRequested: item.hoursRequested,
      requestType: item.timeOffReqstType,
      status: item.status,
      // Convert ISO timestamp to MM/DD/YYYY for display
      dateSubmitted: item.dateSubmitted
        ? formatLegacyDateTime(item.dateSubmitted)
        : '',
      detail: item.detail,
      shift: item.shift,
      unionSeniority: formatSeniorityDate(item.unionSeniority),
      alternateSeniority: formatSeniorityDate(item.altSeniority),
      rank: item.rank,
    }))


  // Calendar view still uses static mock data (calendar API is separate)
  const calendarRequests: EmployeeTimeOffRequest[] = React.useMemo(() => {
    if (!calendarData) {
      return []
    }

    const mapped = mapApproverCalendarViewResponse(calendarData)

    return mapped
  }, [calendarData])
  const { printRef, handlePrint } = usePrint({ fontSize: '9px' })
  const { userInfo, userNuid, primaryApproverInfo } = useProfileDisplay()

  const handleDirectPrint = () => {
    setIsPrinting(true)
    handlePrint()
    setTimeout(() => {
      setIsPrinting(false)
    }, 100)
  }

  const handlePrintWithApi = async () => {
    try {
      setIsPrinting(true)

      if (!lastPayload) {
        setIsPrinting(false)
        return
      }

      const payload = {
        ...lastPayload,
        page: '1',
        perPage: String(totalRecords),
        sortBy: printSortBy ?? lastPayload.sortBy,
        sortOrder: printSortOrder ?? lastPayload.sortOrder,
      }

      const response = await dispatch(
        getApproverPrintRequests(payload)
      ).unwrap()

      setPrintRows(response.items ?? [])

      // Wait for printRows DOM update before opening print dialog
      setTimeout(() => {
        handlePrint()
        setIsPrinting(false)
      }, 0)
    } catch (error) {
      console.error('Print API failed:', error)
      setIsPrinting(false)
    }
  }

  const handleExport = () => {
  if (isCalendarView) return
  if (!lastPayload) return

  const { page, perPage, ...exportPayload } = lastPayload

  dispatch(
    exportApproverRequests({
      payload: {
        ...exportPayload,
        timeZone:
          Intl.DateTimeFormat().resolvedOptions().timeZone,
      },
    })
  )
}
  const handleShowAllRequests = () => {
    if (!filtersConfig) return

    const allCheckboxFilters: Record<string, string[]> = {}

    Object.keys(filtersConfig).forEach((filterKey) => {
      const group = filtersConfig[filterKey]

      if (group?.labels?.length) {
        allCheckboxFilters[filterKey] = [...group.labels]
      }
    })

    const showAllFilter: FilterData = {
      startDate: '',
      endDate: '',
      checkboxFilters: allCheckboxFilters,
    }

    handleSetAppliedFilter(showAllFilter)
    setIsShowingAllRequests(true)
    handleSetDateFilterMode('all')
    setIsFilterOpen(false)
    setCalendarStartDate('')
    const nextWeek = new Date()
    nextWeek.setDate(nextWeek.getDate() + 7)
     setCalendarWeekStart(nextWeek)
    const payload = buildShowAllApproverPayload({
      page: 1,
      perPage: pageSize,
      sortOrder: sortOrder,
    })

    payload.sortBy = sortBy
    dispatch(getApproverRequests(payload))
  }
  /** Re-fetch the requested page while keeping   the current filter and sort. */
  const handlePageChange = (page: number) => {
    if (lastPayload) {
      dispatch(
        getApproverRequests({
          ...lastPayload,
          page: String(page),
          sortBy: lastPayload.sortBy,
          sortOrder: lastPayload.sortOrder,
        })
      )
    } else {
      fetchRequests(page, reduxAppliedFilter, reduxDateFilterMode, sortBy, sortOrder)
    }
  }
  const selectedApproverWeek = React.useMemo(() => {
    if (calendarData?.weekStart) {
      return new Date(`${calendarData.weekStart}T00:00:00`)
    }

    return calendarWeekStart
  }, [calendarData, calendarWeekStart])
 const handleWeekChange = (  weekStart: Date, selectedDate?: Date) => {
setCalendarWeekStart(weekStart)
// Only update the date picker when the user actually selected a date.
if (selectedDate) {
  setCalendarStartDate(
    `${selectedDate.getMonth() + 1}/${selectedDate.getDate()}/${selectedDate.getFullYear()}`
  )
      setIsShowingAllRequests(false)
}
  // When in range mode, update the persisted filter's startDate to match
  // the header-selected week while preserving any existing endDate. Use
  // the freshly computed filter immediately for the outgoing payload to
  // avoid stale Redux reads on first-click.
  let filterForPayload: FilterData | null = reduxAppliedFilter ?? null
  if (reduxDateFilterMode === 'range') {
    const sourceDate = selectedDate ?? weekStart
    const formatted = `${sourceDate.getMonth() + 1}/${sourceDate.getDate()}/${sourceDate.getFullYear()}`
    // If the user explicitly selected a date (via datepicker/search), mirror
    // legacy behavior and set both startDate and endDate to that date. If the
    // user merely navigated the week header, preserve an existing endDate so
    // the user can manage a multi-day range via the filter component.
    const newFilter: FilterData = {
      ...(reduxAppliedFilter ?? { startDate: '', endDate: '', checkboxFilters: {} }),
      startDate: formatted,
      endDate: selectedDate ? formatted : (reduxAppliedFilter?.endDate ?? formatted),
      checkboxFilters: reduxAppliedFilter?.checkboxFilters ?? ({} as Record<string, string[]>),
    }

    handleSetAppliedFilter(newFilter)
    filterForPayload = newFilter
  }

  const payload = buildApproverCalendarPayload({
    filterData: filterForPayload,
    dateMode: reduxDateFilterMode,
    selectedWeekDate: weekStart,
  })

  dispatch(getApproverCalendarView(payload))
}
  /**
   * Re-fetch from page 1 with the new sort column and direction.
   * Only columns with a corresponding API sort field trigger a server sort.
   */
  const handleSort = (columnKey: string, direction: 'ASC' | 'DESC') => {
    const apiSortBy = COLUMN_KEY_TO_SORT_BY[columnKey]

    setActiveSortColumn(columnKey)
    setActiveSortDirection(direction)

    setPrintSortBy(apiSortBy)
    setPrintSortOrder(direction)

    fetchRequests(1, reduxAppliedFilter, reduxDateFilterMode, apiSortBy, direction)
  }


  const columns = getColumns()
  const columnsForPrint = getColumnsForPrint()
  const rows = mapItemsToRows(items)

  // Render view toggle buttons
  const renderViewToggle = () => (
    <div className="flex items-center gap-2">
      <button
        type="button"
        onClick={() => {
          if (view !== 'list') {
            setIsFilterOpen(false)
            setView('list')
          }
        }}
        className={`${view === 'list' ? 'p3 -medium inky' : 'p3 -book'} inline-flex items-center gap-2 ${view === 'list' ? 'opacity-100' : 'opacity-80 hover:opacity-100'} ${view === 'list' ? 'cursor-default' : 'cursor-pointer'}`}
        aria-pressed={view === 'list'}
        aria-disabled={view === 'list'}
        style={{ color: view === 'list' ? '#0D1C3D' : '#0074AD' }}
      >
        <img
          src={listIcon}
          alt="List Icon"
          className="inline-block"
          style={{
            filter:
              view === 'list' ? 'grayscale(100%) brightness(70%)' : 'none',
          }}
        />
        <span className={view === 'list' ? '' : 'underline hover:no-underline'}>
          List View
        </span>
      </button>
      <span className="text-gray-400">|</span>
      <button
        type="button"
        onClick={() => {
          if (view !== 'calendar') {
            setIsFilterOpen(false)
            setView('calendar')
          }
        }}
        className={`${view === 'calendar' ? 'p3 -medium inky' : 'p3 -book'} inline-flex items-center gap-2 ${view === 'calendar' ? 'opacity-100' : 'opacity-80 hover:opacity-100'} ${view === 'calendar' ? 'cursor-default' : 'cursor-pointer'}`}
        aria-pressed={view === 'calendar'}
        aria-disabled={view === 'calendar'}
        style={{ color: view === 'calendar' ? '#0D1C3D' : '#0074AD' }}
      >
        <img
          src={calendarIcon}
          alt="Calendar Icon"
          className="inline-block"
          style={{
            filter:
              view === 'calendar' ? 'grayscale(100%) brightness(70%)' : 'none',
          }}
        />
        <span
          className={view === 'calendar' ? '' : 'underline hover:no-underline'}
        >
          Calendar Week View
        </span>
      </button>
    </div>
  )

  // Render action buttons
  const renderActionButtons = () => (
    <div className="flex flex-col md:flex-row items-stretch md:items-center justify-start md:justify-end w-full md:w-auto gap-2">
      <button
        type="button"
        className="button -secondary flex items-center w-full md:w-auto !bg-[#F2F8FB] hover:!bg-[#003B71] active:!text-[#003B71] focus:!text-[#003B71]"
        onClick={isCalendarView ? handleDirectPrint : handlePrintWithApi}
        disabled={isPrinting}
        title="Print"
      >
        <img src={Print} alt="Print Icon" className="inline-block mr-2" />
        <span className="text-sm leading-5">Print</span>
      </button>
      { !isCalendarView &&  (
      <button
        type="button"
        className={`button -secondary flex items-center w-full md:w-auto !bg-[#F2F8FB] hover:!bg-[#003B71] focus:!text-[#003B71] focus:!bg-[#F2F8FB] active:!bg-[#F2F8FB] active:!text-[#003B71]  ${isCalendarView ? 'opacity-50 cursor-not-allowed' : ''}`}
        onClick={handleExport}
        title="Export File"
        disabled={isCalendarView}
      >
        <img src={exportIcon} alt="Export Icon" className="inline-block mr-2" />
        <span className="text-sm leading-5">Export File</span>
      </button>)}
    </div>
  )
  // Render filter actions toolbar
  const renderFilterActions = () => (
    <div className="flex flex-col md:flex-row items-start md:items-center justify-start md:justify-between gap-4 md:gap-0 mt-4! mb-4 px-4 md:px-1 py-2">
      <div className="flex flex-col md:flex-row items-start md:items-center justify-start gap-4 md:gap-8 w-full md:w-auto">
        {isFilterOpen ? null : (
          <button
            type="button"
            className={`flex items-center gap-2 px-4 py-1 w-full md:w-auto justify-start ${
              isShowingAllRequests
                ? 'cursor-not-allowed'
                : 'hover:opacity-80 cursor-pointer'
            }`}
            disabled={isShowingAllRequests}
            onClick={handleShowAllRequests}
          >
            <img
              src={eyeIcon}
              alt="Show All Requests"
              className="inline-block w-5 h-5"
              style={
                isShowingAllRequests ? { filter: 'brightness(0)' } : undefined
              }
            />

            <span
              className={`p2 -medium ${
                isShowingAllRequests ? 'text-[#0D1C3D]' : 'text-[#0074AD]'
              }`}
            >
              Show All Requests
            </span>
          </button>
        )}

        <button
          type="button"
          className="flex items-center gap-2 px-4 py-1 w-full md:w-auto justify-start"
          onClick={() => setIsFilterOpen(!isFilterOpen)}
        >
          <img
            src={isFilterOpen ? minusIcon : plusIcon}
            alt={isFilterOpen ? 'Minus Icon' : 'Plus Icon'}
            className="inline-block shrink-0"
          />
          <span className="p2 -medium text-[#003B71]">Filter Requests</span>
        </button>
      </div>
      {!isCalendarView && !isFilterOpen && (
        <button
          type="button"
          className="hidden md:flex items-center gap-2 px-4 py-1 w-full md:w-auto justify-start md:justify-end"
          onClick={() => setShowSeniority(!showSeniority)}
          title={
            showSeniority
              ? 'Hide Seniority & Vac Bid Rank'
              : 'Show Seniority & Vac Bid Rank'
          }
        >
          <img
            src={showSeniority ? eyeOffIcon : eyeIcon}
            alt={showSeniority ? 'Hide' : 'Show'}
            className="w-5 h-5"
          />
          <span className="p2 -medium text-[#0074AD]">
            {showSeniority ? 'Hide' : 'Show'} Seniority & Vac Bid Rank
          </span>
        </button>
      )}
    </div>
  )

  // Render the list or calendar view content
  const renderContent = () => {
    if (isCalendarView) {
      if (calendarLoading) {
        return <Loader />
      }

      if (calendarData === null) {
        return (
          <ValidationBanner
            variant="error"
            heading="Error"
            message="Unable to load calendar data."
          />
        )
      }

      return (
        <>
          <div className="w-full flex justify-center md:justify-end mb-6!">
            <CalendarStatusInfo />
          </div>
          <CalendarWeekView
            requests={calendarRequests}
            onWeekChange={handleWeekChange}
            isApproverFlow={true}
            selectedWeekStart={selectedApproverWeek}
            approverStartDate={calendarStartDate}
             onApproverStartDateChange={setCalendarStartDate}
            onRequestClick={(requestId) =>
              navigate(`/approver/process/${requestId}`)
            }
          />
        </>
      )
    }

    if (requestsError) {
      return (
        <div className="flex items-center justify-center min-h-96 py-12">
          <ValidationBanner
            variant="error"
            heading="Error"
            message={requestsError}
          />
        </div>
      )
    }

    return (
      <div className="mt-6">
        {(requestsLoading || exportLoading || isPrinting) && <Loader />}
        {!requestsLoading && rows.length === 0 && (
          <div className="flex items-center justify-center min-h-96 py-12">
            <div className="text-center">
              <p className="p3 -medium">
                There are no Time Off Requests to process.
              </p>
            </div>
          </div>
        )}
        {rows.length > 0 && (
          <Table
            columns={columns}
            rows={rows}
            isSorting={true}
            showPagination={true}
            paginationProps={{
              totalItems: totalRecords,
              itemsPerPage: pageSize,
            }}
            currentPage={currentPage}
            onPageChange={handlePageChange}
            onSort={handleSort}
            sortColumn={activeSortColumn}
            sortDirection={activeSortDirection}
          />
        )}
      </div>
    )
  }

  return (
    <div className="space-y-6 print-container">
      <style>
        {`
#temp-print-container {
  display: none;
}

.print-only-header {
  display: none;
}

@media print {
  @page {
    size: landscape;
    margin: 10mm;
  }

  .print-only-header {
    display: block !important;
    margin-bottom: 16px;
    padding-bottom: 8px;
    border-bottom: 1px solid #ccc;
  }

  /* Hide normal screen content */
  .approver-history-print,
  .history-filter-section,
  button {
    display: none !important;
  }

  /* Show print table */
  #temp-print-container {
    display: block !important;
    width: 100% !important;
    max-width: 100% !important;
    overflow: visible !important;
  }

  /* Remove scroll/fixed sizing from shared Table */
  #temp-print-container,
  #temp-print-container * {
    overflow: visible !important;
  }

  #temp-print-container table {
    width: 100% !important;
    max-width: 100% !important;
    table-layout: auto !important;
    border-collapse: collapse !important;
  }

  /* Repeat header on every printed page */
  #temp-print-container thead {
    display: table-header-group !important;
  }

  #temp-print-container tbody {
    display: table-row-group !important;
  }

  #temp-print-container tr {
    page-break-inside: avoid !important;
  }

  #temp-print-container th {
  white-space: normal !important;
  word-break: break-word !important;
  padding: 6px !important;
  font-size: 13px !important;
  font-weight: 700 !important;
}

#temp-print-container td {
  white-space: normal !important;
  word-break: break-word !important;
  padding: 4px !important;
  font-size: 9px !important;
}
  /* Hide sorting arrows in print */
  #temp-print-container .arrows-table {
    display: none !important;
  }

  /* Remove pagination area from print */
  #temp-print-container .pagination {
    display: none !important;
  }
}
  @media print {
  @page {
    size: landscape;
    margin: 10mm;
  }

  #temp-print-container {
    display: block !important;
    width: 100% !important;
    overflow: visible !important;
  }

  #temp-print-container table {
    width: 100% !important;
    table-layout: fixed !important;
    border-collapse: collapse !important;
  }

  /* Repeat table header on every page */
  #temp-print-container thead {
    display: table-header-group !important;
  }

  #temp-print-container tr {
    page-break-inside: avoid !important;
  }

  /* Important: keep header content visible */
  #temp-print-container th {
    display: table-cell !important;
    visibility: visible !important;
    overflow: visible !important;
    white-space: normal !important;
  }

  #temp-print-container th img {
    display: none !important;
  }

  #temp-print-container th span {
    display: inline !important;
    visibility: visible !important;
  }

  /* Drop screen-only width/min/max constraints so the fixed layout can share
     the page evenly, and force every cell to wrap instead of overflowing */
  #temp-print-container th,
  #temp-print-container td {
    width: auto !important;
    max-width: none !important;
    min-width: 0 !important;
    white-space: normal !important;
    overflow-wrap: anywhere !important;
    word-break: break-word !important;
    vertical-align: top !important;
  }

  /* Same for any nested wrappers/links the Table renders inside a cell */
  #temp-print-container td *,
  #temp-print-container th * {
    max-width: 100% !important;
    min-width: 0 !important;
    white-space: normal !important;
    overflow-wrap: anywhere !important;
    word-break: break-word !important;
  }

  #temp-print-container td a {
    display: block !important;
  }

  /* Employee (NUID) holds the longest values, so give it extra room */
  #temp-print-container th:nth-child(1),
  #temp-print-container td:nth-child(1) {
    width: 18% !important;
  }

  /* Dates Requested holds a full date range, so give it a little extra room */
  #temp-print-container th:nth-child(2),
  #temp-print-container td:nth-child(2) {
    width: 10% !important;
  }

  #temp-print-container td:nth-child(1) > div {
    width: auto !important;
    min-width: 0 !important;
    display: flex !important;
    align-items: flex-start !important;
    gap: 4px !important;
  }

  #temp-print-container td:nth-child(1) > div > span {
    min-width: 0 !important;
    white-space: normal !important;
    overflow-wrap: anywhere !important;
    word-break: break-word !important;
    flex: 1 1 auto !important;
  }

  #temp-print-container td:nth-child(1) > div > div {
    width: auto !important;
    min-width: 0 !important;
    flex: 0 0 auto !important;
  }
}
`}
      </style>

      {/* Header: View Toggle LEFT / Print & Export RIGHT */}
      <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-3 md:gap-0 mb-4 !pb-6">
        <div className="flex flex-col md:flex-row md:items-center gap-3 w-full md:w-auto">
          {renderViewToggle()}
        </div>
        {renderActionButtons()}
      </div>

      {/* Filter Actions toolbar */}
      {renderFilterActions()}

      {/* Filter Component — shown when isFilterOpen */}
      {isFilterOpen && (
        <div className="mb-4 relative md:left-6">
          {/* Non-blocking warning when filter dropdown options could not be loaded */}
          {filterOptionsError && (
            <div className="mb-4">
              <ValidationBanner
                variant="info"
                heading="Filter options unavailable"
                message="Could not load filter options from the server. Default options are shown."
              />
            </div>
          )}
          <div className="mb-3">
            {filterError && (
              <div ref={filterErrorRef} className="mb-4">
                <ValidationBanner
                  variant="error"
                  heading="Error"
                  message={filterError}
                />
              </div>
            )}
            <div className="p2 -medium mt-4!">Date</div>
            <div className="flex flex-col items-start gap-2 mt-2">
              <label className="inline-flex items-center gap-2">
                <input
                  type="radio"
                  name="dateFilter"
                  value="all"
                  checked={reduxDateFilterMode === 'all'}
                  onChange={() => handleSetDateFilterMode('all')}
                />
                <span className="p2">All</span>
              </label>
              <label className="inline-flex items-center gap-2 mb-4!">
                <input
                  type="radio"
                  name="dateFilter"
                  value="range"
                  checked={reduxDateFilterMode === 'range'}
                  onChange={() => handleSetDateFilterMode('range')}
                />
                <span className="p2">Date Range</span>
              </label>
            </div>
          </div>
          <div className="!pb-6">
            <FilterComponent
              requiredFilters={[
                {
                  key: 'requestTypeFilter',
                  message: 'Please select at least one Request Type.',
                },
                {
                  key: 'statusFilter',
                  message: 'Please select at least one Request Status.',
                },
              ]}
              onApplyFilter={(filterData) => {
                // Clear previous errors
                setFilterError(null)

                handleSetAppliedFilter(filterData)
                setIsShowingAllRequests(false)
                setIsFilterOpen(false)
                fetchRequests(1, filterData, reduxDateFilterMode, sortBy, sortOrder)
                const currentDateMode = reduxDateFilterMode

                if (view === 'calendar') {
                   if (currentDateMode === 'range' && filterData.startDate) {
                     setCalendarStartDate(filterData.startDate)
                   }
                  fetchCalendar(filterData, currentDateMode)
                }
              }}
              onCancel={() => {
                setFilterError(null)
                setIsFilterOpen(false)
              }}
              onReset={() => {
                setIsFilterOpen(false)
                handleSetAppliedFilter(defaultInitialFilter ?? null)
                setIsShowingAllRequests(false)
                if (isShowingAllRequests || reduxAppliedFilter) {
                  handleSetDateFilterMode('all')
                  fetchRequests(
                    1,
                    defaultInitialFilter ?? null,
                    'all',
                    sortBy,
                    sortOrder
                  )
                }
              }}
              initialFilter={reduxAppliedFilter ?? defaultInitialFilter}
              key={JSON.stringify(reduxAppliedFilter)}
              dateMode={reduxDateFilterMode}
              filtersConfig={filtersConfig}
            />
          </div>
          <hr className="-variation-1" />
        </div>
      )}
      {/* Filters applied summary badge */}
      {reduxAppliedFilter &&
        (() => {
          const checkboxFilters = reduxAppliedFilter.checkboxFilters || {}

          const hasSelection = (arr?: string[]) => {
            if (!arr || arr.length === 0) return false
            return !arr.some((v) => /all/i.test(v))
          }

          const selectedHeaders = [
            [checkboxFilters.departmentFilter, 'Department'],
            [checkboxFilters.unionAffiliationFilter, 'Union'],
            [checkboxFilters.approvalGroupFilter, 'Approver'],
            [checkboxFilters.jobCodeFilter, 'Job Code'],
            [checkboxFilters.statusFilter, 'Request Status'],
            [checkboxFilters.requestTypeFilter, 'Request Type'],
          ]
            .filter(([filter]) => hasSelection(filter as string[]))
            .map(([,label]) => label as string)

          if (reduxAppliedFilter.startDate && reduxAppliedFilter.endDate) {
            selectedHeaders.push('Date Range')
          }
          if (selectedHeaders.length === 0) return null

          return (
            <div
              className="mb-6! mt-6! p-6! rounded"
              style={{ background: '#FEF0E7' }}
            >
              <LabeledValue
                label="Filters Applied"
                value={selectedHeaders.join(', ')}
              />
            </div>
          )
        })()}

      {/* List / Calendar content */}
      <div ref={isCalendarView ? printRef : undefined}>
        {isCalendarView && (
          <div className="print-only-header">
            <p style={{margin: '0 0 4px 0', fontWeight: 'bold', fontSize: '14px'}}>{userInfo}{userNuid}</p>
            <p style={{margin: '0 0 4px 0', fontSize: '12px'}}>Primary Approver: {primaryApproverInfo}</p>
            <p style={{margin: '0', fontSize: '12px'}}>Incoming Time Off Requests</p>
          </div>
        )}
        {renderContent()}
      </div>
      {!isCalendarView && printRows.length > 0 && (
        <div id="temp-print-container" ref={printRef}>
          <div style={{marginBottom: '16px', paddingBottom: '8px', borderBottom: '1px solid #ccc'}}>
            <p style={{margin: '0 0 4px 0', fontWeight: 'bold', fontSize: '14px'}}>{userInfo}{userNuid}</p>
            <p style={{margin: '0 0 4px 0', fontSize: '12px'}}>Primary Approver: {primaryApproverInfo}</p>
            <p style={{margin: '0', fontSize: '12px'}}>Incoming Time Off Requests</p>
          </div>
          <Table
            columns={columnsForPrint}
            rows={mapItemsToRows(printRows)}
            isSorting={false}
            showPagination={false}
          />
        </div>
      )}
    </div>
  )
}

export default ApproverRequests
